'use client';

import React, { useId } from 'react';
import { cn } from '../lib/utils';
import { SectionTitle } from './typography';
import { useCompositionDensity, type CompositionDensity } from './composition-density';

export interface SectionCardProps {
  title: string;
  /** Keep descriptions to one short sentence; longer explanations belong in AboutThisPage. */
  description?: string;
  /** Structured label/value metadata, usually a KeyFacts description list. */
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  footer?: { primary?: React.ReactNode; secondary?: React.ReactNode };
  children: React.ReactNode;
  className?: string;
  density?: CompositionDensity;
}

/** A single content boundary; callers must not nest SectionCards. */
export function SectionCard({
  title,
  description,
  meta,
  actions,
  footer,
  children,
  className,
  density: densityOverride,
}: SectionCardProps) {
  const titleId = useId();
  const density = useCompositionDensity(densityOverride);
  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        'min-w-0 break-words border border-border bg-surface-sheet [overflow-wrap:anywhere]',
        density === 'comfort'
          ? 'rounded-card-comfort p-7 shadow-card-comfort'
          : 'rounded-card p-panel shadow-none',
        className,
      )}
    >
      <header className="mb-4 flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3">
        <div className="min-w-0 flex-[1_1_16rem] space-y-2">
          <SectionTitle id={titleId} className="break-normal [overflow-wrap:break-word]">
            {title}
          </SectionTitle>
          {description && <p className="font-body text-body text-text-muted">{description}</p>}
          {meta != null && (
            <div className="min-w-0 font-body text-body font-medium tabular-nums text-text-heading [&_dt]:text-caption [&_dt]:font-normal [&_dt]:text-text-muted [&_dd]:mt-1">
              {meta}
            </div>
          )}
        </div>
        {actions != null && (
          <div className="ml-auto flex min-w-0 max-w-full flex-[0_1_auto] flex-wrap justify-start gap-2 [overflow-wrap:normal]">
            {actions}
          </div>
        )}
      </header>
      <div className="min-w-0 font-body text-body text-text-body">{children}</div>
      {footer && (footer.primary != null || footer.secondary != null) && (
        <footer className="mt-4 flex min-w-0 flex-col gap-2 border-t border-border pt-4 sm:flex-row sm:flex-wrap sm:items-center">
          {footer.secondary != null && (
            <div className="flex min-w-0 max-w-full flex-wrap gap-2 [overflow-wrap:normal]">
              {footer.secondary}
            </div>
          )}
          {footer.primary != null && (
            <div className="flex w-full min-w-0 max-w-full flex-wrap gap-2 [overflow-wrap:normal] [&>*]:w-full sm:ml-auto sm:w-auto sm:[&>*]:w-auto">
              {footer.primary}
            </div>
          )}
        </footer>
      )}
    </section>
  );
}
